import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import {
  CustomerUser,
  CustomerUsersPage,
  CustomerUserType,
  PermissionModule
} from '../models/customer-user.model';

export interface CreateCustomerUserPayload {
  email: string;
  typeId: number;
  firstName?: string;
  lastName?: string;
  language?: string;
}

export type UpdateCustomerUserPayload = Partial<{
  firstName: string;
  lastName: string;
  language: string;
  typeId: number;
}>;

/**
 * CustomerUsersService — team-member (sub-user) management + self-service.
 * REST: /api/customer-users (guide §6/§7). Management endpoints require a
 * customer (main-account) session; changeOwnPassword is sub-user-only.
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerUsersService {
  private readonly api = inject(ApiService);

  list(page = 1, limit = 20): Observable<CustomerUsersPage> {
    return this.api.get<CustomerUsersPage>('/customer-users', { params: { page, limit } });
  }

  create(payload: CreateCustomerUserPayload): Observable<CustomerUser> {
    return this.api.post<CustomerUser>('/customer-users', payload);
  }

  update(id: number, payload: UpdateCustomerUserPayload): Observable<CustomerUser> {
    return this.api.put<CustomerUser>(`/customer-users/${id}`, payload);
  }

  /** `enabled` must serialize as a real JSON boolean — never a string. */
  setEnabled(id: number, enabled: boolean): Observable<CustomerUser> {
    return this.api.put<CustomerUser>(`/customer-users/${id}/enabled`, { enabled });
  }

  remove(id: number): Observable<{ message: string }> {
    return this.api.delete<{ message: string }>(`/customer-users/${id}`);
  }

  /** Types this customer can assign — for the role dropdown. */
  getTypes(): Observable<CustomerUserType[]> {
    return this.api.get<CustomerUserType[]>('/customer-users/types');
  }

  /** Full assignable module registry (informational "what a role includes"). */
  getPermissionModules(): Observable<PermissionModule[]> {
    return this.api.get<PermissionModule[]>('/customer-users/permission-modules');
  }

  /** Sub-user session only — always allowed regardless of granted modules. */
  changeOwnPassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.api.put<void>('/customer-users/me/password', { currentPassword, newPassword });
  }
}
